import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DragEndEvent } from '@dnd-kit/core';
import { SeatingDnd } from './SeatingDnd';
import { Check, Sparkles, X } from 'lucide-react';
import type { SeatingConflict, SeatingParty } from '@peles/shared';
import { useLockSeating, useSeatingState, useSetPrimarySeating } from '../../hooks/useSeating';
import { useTableMealCounts } from '../../hooks/useFloorPlans';
import { useAppSettingValue } from '../../hooks/useAppSettingValue';
import { extractApiErrorMessage } from '../../utils/apiError';
import { useDialog } from '../../context/DialogContext';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { SeatingPlanControls } from '../floor-plan/SeatingPlanControls';
import { PrintablesButton } from '../printables/PrintablesButton';
import { AutoAssignSeatsModal } from '../floor-plan/AutoAssignSeatsModal';
import { AiSeatingModal } from '../floor-plan/AiSeatingModal';
import type { SeatingArrangement } from '../../api/seating';
import { UnseatedPanel } from './UnseatedPanel';
import { TableCard } from './TableCard';
import { resolveSeatingDrop } from './dropTargets';
import { PartySheet } from './PartySheet';
import { useSeatingActions, useSeatingKeys } from './useSeatingActions';
import { SeatingNoticeBar, UndoRedoButtons } from './SeatingNotice';
import {
  adjustProposal,
  partyIndex,
  projectProposal,
  proposalFrom,
  seatedTotals,
  stillToSeat,
  swapBySide,
  unseatedParties,
  type ResponseFilter,
} from './boardModel';

export interface SeatingBoardProps {
  eventId: string;
  /** May change who sits where. */
  canWrite: boolean;
  /**
   * The office's board (the default): every plan, the lock, the seating-plan
   * choice, pins and AI seating. Off in the client portal, where only the
   * event's seating plan is shown and it is changed only while unlocked.
   */
  office?: boolean;
}

interface Proposal {
  source: string;
  arrangement: SeatingArrangement;
}

/**
 * The seating board: an event's tables and who sits at them, on one of its
 * plans, with the parties still to seat beside them. A party is dragged onto
 * a table — or, on a phone or anywhere a drag is awkward, tapped and then a
 * table or seat tapped. Every change is saved as it is made and can be undone
 * (Ctrl+Z, or "Undo" on the notice). Auto-assign and AI seating are shown
 * here as a proposal, adjusted by hand, then applied.
 */
export function SeatingBoard({ eventId, canWrite, office = true }: SeatingBoardProps) {
  const [planId, setPlanId] = useState<string | null>(null);
  const { data: live, isLoading, error } = useSeatingState(eventId, planId);
  const floorPlanId = live?.plan.id ?? null;

  const lockSeating = useLockSeating(eventId);
  const setPrimary = useSetPrimarySeating(eventId);
  const { data: mealCounts = {} } = useTableMealCounts(eventId);
  // Clients cannot read settings, and AI seating is the office's.
  const aiEnabled =
    useAppSettingValue('ai_seating_enabled', { enabled: office }).data === true && office;
  const actions = useSeatingActions(eventId, live);
  const { confirm } = useDialog();
  const { history, busy, setNotice } = actions;

  const [filter, setFilter] = useState<ResponseFilter>('coming');
  const [selected, setSelected] = useState<string | null>(null);
  const [sheetGuest, setSheetGuest] = useState<string | null>(null);
  const [highlight, setHighlight] = useState<Set<string>>(new Set());
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [showAutoAssign, setShowAutoAssign] = useState(false);
  const [showAi, setShowAi] = useState(false);

  const projected = useMemo(
    () => (live && proposal ? projectProposal(live, proposal.arrangement) : null),
    [live, proposal],
  );
  const state = projected?.state ?? live;
  const moved = projected?.moved ?? new Set<string>();
  const parties = useMemo(
    () => (state ? partyIndex(state) : new Map<string, SeatingParty>()),
    [state],
  );
  const locked = !!live?.plan.seatingLockedAt;
  // A client changes only the seating plan, and only while it is unlocked —
  // the server refuses anything else; the board does not offer it.
  const clientMayWrite = office || (!!live?.plan.isPrimarySeating && !locked);
  const editable = canWrite && clientMayWrite;
  const writable = editable && !!live;

  const choosePlan = useCallback(
    (id: string) => {
      setPlanId(id);
      setProposal(null);
      setSelected(null);
      history.reset();
    },
    [history],
  );

  /**
   * A proposal re-seats everyone but pinned seats, so a pinned party cannot be
   * moved within one: it would keep its pinned seat and take another.
   */
  const refusePinned = useCallback(
    (ids: string[]) => {
      const pinned = ids.find((id) =>
        state?.tables.some((t) => t.occupied.some((s) => s.guestId === id && s.locked)),
      );
      if (!pinned) return false;
      setNotice({
        text: `${parties.get(pinned)?.name ?? 'That party'} is pinned — unpin them before moving them in a proposal`,
        tone: 'error',
      });
      return true;
    },
    [state, parties, setNotice],
  );

  /** A table's men's or women's section, or null for a mixed one. */
  const sectionAt = useCallback(
    (tableUid: string | null) =>
      tableUid ? (live?.tables.find((t) => t.uid === tableUid)?.section ?? null) : null,
    [live],
  );

  /** Seat a party — into the proposal while one is on screen, else for real. */
  const seatParty = useCallback(
    async (guestId: string, tableUid: string | null, seatNumber?: number) => {
      setSelected(null);
      const party = parties.get(guestId);
      if (!party) return;
      if (proposal) {
        if (refusePinned([guestId])) return;
        setProposal({
          ...proposal,
          arrangement: adjustProposal(
            proposal.arrangement,
            party,
            tableUid,
            seatNumber,
            sectionAt(tableUid),
          ),
        });
        return;
      }
      await actions.seat(guestId, tableUid, seatNumber);
    },
    [parties, proposal, actions, refusePinned, sectionAt],
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const drop = resolveSeatingDrop(parties, event.active.id, event.over?.id);
      if (drop) void seatParty(drop.guestId, drop.tableUid, drop.seatNumber);
    },
    [parties, seatParty],
  );

  /** Two parties trade places — in the proposal while one is on screen. */
  const swapParties = useCallback(
    async (guestId: string, otherId: string) => {
      if (!proposal || !state) {
        await actions.swap(guestId, otherId);
        return;
      }
      if (refusePinned([guestId, otherId])) return;
      const bySide = swapBySide(proposal.arrangement, guestId, otherId, (uid) => sectionAt(uid));
      if (bySide) {
        setProposal({ ...proposal, arrangement: bySide });
        return;
      }
      const firstSeat = (id: string) => {
        for (const table of state.tables) {
          const seats = table.occupied.filter((s) => s.guestId === id).map((s) => s.seatNumber);
          if (seats.length) return { tableUid: table.uid, seatNumber: Math.min(...seats) };
        }
        return null;
      };
      const a = firstSeat(guestId);
      const b = firstSeat(otherId);
      const partyA = parties.get(guestId);
      const partyB = parties.get(otherId);
      if (!a || !b || !partyA || !partyB) return;
      const moved = adjustProposal(
        proposal.arrangement,
        partyA,
        b.tableUid,
        b.seatNumber,
        sectionAt(b.tableUid),
      );
      setProposal({
        ...proposal,
        arrangement: adjustProposal(moved, partyB, a.tableUid, a.seatNumber, sectionAt(a.tableUid)),
      });
    },
    [proposal, state, parties, actions, refusePinned, sectionAt],
  );

  const applyProposal = useCallback(async () => {
    if (!projected) return;
    const full = projected.state.conflicts.filter((c) => c.kind === 'OVER_CAPACITY');
    if (
      full.length > 0 &&
      !(await confirm({
        title: full.length === 1 ? 'A table is over capacity' : 'Tables are over capacity',
        message: `${full.map((c) => c.message).join('. ')}. Apply the arrangement anyway?`,
        confirmLabel: 'Apply anyway',
        variant: 'warning',
      }))
    ) {
      return;
    }
    const done = await actions.arrange(
      projected.arrangement,
      `Applied the ${proposal?.source ?? 'proposed'} arrangement`,
    );
    if (done) setProposal(null);
  }, [projected, proposal, actions, confirm]);

  const putDown = useCallback(() => setSelected(null), []);
  useSeatingKeys({
    enabled: true,
    undoEnabled: !proposal,
    onUndo: () => void actions.undo(),
    onRedo: () => void actions.redo(),
    onEscape: putDown,
  });

  const onConflictSelect = useCallback(
    (conflict: SeatingConflict) => {
      if (!live) return;
      const uids = conflict.tableUid
        ? [conflict.tableUid]
        : live.tables
            .filter((t) => t.occupied.some((s) => conflict.guestIds.includes(s.guestId)))
            .map((t) => t.uid);
      setHighlight(new Set(uids));
      if (uids[0])
        document.getElementById(`seating-table-${uids[0]}`)?.scrollIntoView?.({ block: 'center' });
      else if (conflict.guestIds[0]) setSelected(conflict.guestIds[0]);
    },
    [live],
  );

  if (isLoading) {
    return (
      <div className="h-64 animate-pulse rounded-2xl bg-gray-100" data-testid="seating-loading" />
    );
  }
  if (error || !state || !live) {
    // An event with no plan to seat on answers 404 — the board says what to do.
    const missing =
      !error || (error as { response?: { status?: number } }).response?.status === 404;
    return (
      <div
        className="rounded-2xl border border-gray-200 bg-white p-8 text-center"
        data-testid="seating-empty"
      >
        <p className="text-sm text-gray-700">
          {missing
            ? office
              ? 'This event has no floor plan yet. Draw its tables first, then seat guests here.'
              : 'The tables are not ready yet. Seating opens here once the floor plan is drawn.'
            : extractApiErrorMessage(error, 'Could not load the seating')}
        </p>
        {office && (
          <Link
            to={`/events/${eventId}/floor-plan`}
            className="mt-3 inline-block text-sm text-primary-700 hover:underline"
          >
            Open the floor plan
          </Link>
        )}
      </div>
    );
  }

  const { seated, expected } = seatedTotals(live);
  const unseated = unseatedParties(state, filter);
  const selectedParty = selected ? parties.get(selected) : undefined;
  const picking = selectedParty
    ? {
        guestId: selectedParty.guestId,
        size: stillToSeat(selectedParty) || selectedParty.partySize,
      }
    : null;
  const tableMeals = (tableNumber: number | null) =>
    live.plan.isPrimarySeating && tableNumber != null ? mealCounts[tableNumber] : undefined;

  return (
    <div className="space-y-3" data-testid="seating-board">
      <div className="flex flex-wrap items-center gap-2">
        {office && (
          <div className="w-56">
            <SearchableListSelect
              items={live.plans}
              value={live.plan.id}
              onChange={(value) => choosePlan(value as string)}
              getValue={(plan) => plan.id}
              getLabel={(plan) => plan.name}
              getSubtitle={(plan) => (plan.isPrimarySeating ? 'Seating plan' : null)}
              ariaLabel="Floor plan"
            />
          </div>
        )}
        <span className="text-sm text-gray-700" data-testid="seating-summary">
          <strong>{seated}</strong>/{expected} seated
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <SeatingPlanControls
            state={live}
            onMakePrimary={() =>
              floorPlanId &&
              setPrimary.mutate(floorPlanId, {
                onError: (e) =>
                  setNotice({
                    text: extractApiErrorMessage(e, 'Could not change the seating plan'),
                    tone: 'error',
                  }),
              })
            }
            onToggleLock={(value) =>
              floorPlanId &&
              lockSeating.mutate(
                { floorPlanId, locked: value },
                {
                  onError: (e) =>
                    setNotice({
                      text: extractApiErrorMessage(e, 'Could not change the lock'),
                      tone: 'error',
                    }),
                },
              )
            }
            busy={lockSeating.isPending || setPrimary.isPending || !canWrite}
            onConflictSelect={onConflictSelect}
            office={office}
          />
          {office && <PrintablesButton eventId={eventId} chooseInvitation />}
          {editable && !proposal && (
            <>
              <button
                type="button"
                onClick={() => setShowAutoAssign(true)}
                className="flex items-center gap-1 rounded-md border border-purple-300 px-3 py-2 text-sm text-purple-700 hover:bg-purple-50"
              >
                <Sparkles size={14} /> Auto-assign
              </button>
              {aiEnabled && (
                <button
                  type="button"
                  onClick={() => setShowAi(true)}
                  className="flex items-center gap-1 rounded-md border border-purple-300 px-3 py-2 text-sm text-purple-700 hover:bg-purple-50"
                >
                  <Sparkles size={14} /> AI seating
                </button>
              )}
              <UndoRedoButtons actions={actions} />
            </>
          )}
        </div>
      </div>

      {locked && canWrite && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {office
            ? 'Seating is locked for printing. The office can still change it; clients cannot.'
            : 'Seating is locked for printing. Ask the office if something needs to change.'}
        </p>
      )}

      {proposal && projected && (
        <div
          className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          data-testid="seating-proposal"
        >
          <span className="flex-1">
            <strong>{proposal.source} proposal</strong> — {projected.moved.size}{' '}
            {projected.moved.size === 1 ? 'party' : 'parties'} placed. Drag to adjust; nothing is
            saved until you apply it.
          </span>
          <button
            type="button"
            onClick={() => void applyProposal()}
            disabled={busy}
            className="flex items-center gap-1 rounded-md bg-primary-600 px-3 py-1.5 text-white hover:bg-primary-700 disabled:opacity-50"
          >
            <Check size={14} /> Apply
          </button>
          <button
            type="button"
            onClick={() => setProposal(null)}
            className="flex items-center gap-1 rounded-md border border-amber-300 px-3 py-1.5 hover:bg-amber-100"
          >
            <X size={14} /> Discard
          </button>
        </div>
      )}

      {selectedParty && (
        <p className="text-sm text-primary-800" role="status">
          {selectedParty.name} picked up — tap a table or a seat to put them there, or Escape to put
          them down.
        </p>
      )}

      <SeatingDnd parties={parties} onDragEnd={onDragEnd}>
        <div className="grid gap-3 lg:grid-cols-[18rem_1fr]">
          <UnseatedPanel
            parties={unseated}
            filter={filter}
            onFilterChange={setFilter}
            selectedGuestId={selected}
            proposed={moved}
            canWrite={writable}
            onSelect={(guestId) => setSelected((current) => (current === guestId ? null : guestId))}
          />
          {state.tables.length === 0 ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-600">
              This plan has no numbered tables with seats yet.
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="seating-tables">
              {state.tables.map((table) => (
                <TableCard
                  key={table.uid}
                  table={table}
                  parties={parties}
                  picking={picking}
                  selectedGuestId={selected}
                  proposed={moved}
                  highlighted={highlight.has(table.uid)}
                  canWrite={writable}
                  meals={tableMeals(table.tableNumber)}
                  onSeatHere={(seatNumber) =>
                    selected && void seatParty(selected, table.uid, seatNumber)
                  }
                  onPartyClick={(guestId) => {
                    if (selected && selected !== guestId) {
                      void seatParty(selected, table.uid);
                    } else if (selected === guestId) {
                      setSelected(null);
                    } else {
                      setSheetGuest(guestId);
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </SeatingDnd>

      <PartySheet
        party={sheetGuest ? (parties.get(sheetGuest) ?? null) : null}
        tables={state.tables}
        parties={parties}
        canWrite={writable}
        busy={busy}
        onClose={() => setSheetGuest(null)}
        onMove={(tableUid) => {
          const guestId = sheetGuest;
          setSheetGuest(null);
          if (guestId) void seatParty(guestId, tableUid);
        }}
        onSwap={(other) => {
          const guestId = sheetGuest;
          setSheetGuest(null);
          if (guestId) void swapParties(guestId, other);
        }}
        onPin={
          office
            ? (value) => {
                if (sheetGuest && !proposal) void actions.pin(sheetGuest, value);
                setSheetGuest(null);
              }
            : undefined
        }
        onMoveMember={(memberId, tableUid) => {
          if (sheetGuest && !proposal) void actions.seatMember(sheetGuest, memberId, tableUid);
        }}
      />

      <SeatingNoticeBar actions={actions} />

      {floorPlanId && showAutoAssign && (
        <AutoAssignSeatsModal
          open
          floorPlanId={floorPlanId}
          onClose={() => setShowAutoAssign(false)}
          hasSections={!!state?.tables.some((t) => t.section)}
          onPreview={(result) => {
            setSelected(null);
            setProposal({
              source: 'Auto-assign',
              arrangement: proposalFrom(live, result),
            });
          }}
        />
      )}
      {floorPlanId && showAi && (
        <AiSeatingModal
          open
          floorPlanId={floorPlanId}
          onClose={() => setShowAi(false)}
          onPreview={(result) => {
            setSelected(null);
            setProposal({ source: 'AI', arrangement: proposalFrom(live, result) });
          }}
        />
      )}
    </div>
  );
}
